// #region Imports
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import CommentIcon from "@mui/icons-material/Comment";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { ThemeProvider, styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { MaskedTeam } from "work/masked-fields";
import { Content, ProposalTab, TabContent, TabsContainer } from "work/_views/work-page-styled-components";
import {
  ProposalPageContainer as PageContainer,
  ProposalPageHeader as HeaderContainer,
  ProposalPageMain,
  ProposalPageTitle as PageTitle,
  ProposalPageTitleBreadcrumb as PageTitleBreadcrumb,
  ProposalPageTitleContainer as TitleContainer,
  ProposalSidePanelColumn as SidePanelColumn,
  ProposalStickyPageHeader as StickyPageHeader,
  ProposalTabsHeader,
} from "work/proposals/_views/_shared/proposal-styled-components";
import {
  PROPOSAL_SIDE_PANEL_WIDTH,
  useProposalPageCanSeatSidePanel,
} from "work/proposals/_views/_shared/proposal-page-panels";
import { paneFlushContent } from "work/_views/work-pane-layout";
import useWorkPaneTheme from "work/_views/work-pane-typography";
import useWorkPaneSize, {
  WorkPaneWidthContext,
} from "work/_views/use-work-pane-size";
import { useGetProposalByIdQuery } from "work/proposals/_api/proposal-api";
import { getServerProposalById, isProposalLoadIssue } from "work/proposals/_store/proposals-redux-slice";
import ArchivedBadge from "work/proposals/_views/archive/archived-badge";
import ConflictsTab from "work/proposals/_views/archive/tabs/archived-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/archive/tabs/archived-proposal-details-tab";
import ClientTeamTab from "work/proposals/_views/archive/tabs/archived-proposal-client-team-tab";
import DiscountTab from "work/proposals/_views/archive/tabs/archived-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/archive/tabs/archived-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/archive/tabs/archived-proposal-policies-tab";
import TeamTab from "work/proposals/_views/archive/tabs/archived-proposal-team-tab";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import Comments from "work/proposals/comments/_view/comments";
import {
  getCommentThreadsByProposal,
  getErrorLoadingCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  HumanReadableProposalFieldName,
  ProposalFieldCategory,
} from "work/proposals/proposal-fields/proposal-field";
// #endregion

/**
 * The comments pane's width. It is reserved in the page's row rather than only given to the drawer:
 * a persistent Material drawer paints over the pane it sits beside, so the page used to lay its
 * content out against the full width and let the pane hide the right edge of it — three date fields
 * on Details lost 110px each on a 1280px display (#4964). Where the row cannot seat both, the pane
 * goes back to covering the content rather than squeezing it to a sliver — see
 * `useProposalPageCanSeatSidePanel`.
 */
const DRAWER_WIDTH = PROPOSAL_SIDE_PANEL_WIDTH;

// #region Styled Components
/**
 * The archived page's own padding over the shared pane. Horizontal padding is deliberately not part of
 * it: the pane declares one gutter for everything it holds (`workPaneGutters`), and adding a
 * second here would double it. Everything else — the CSS container the tabs and their fields measure
 * themselves against, and the compact treatment that container drives — comes from `ProposalPageMain`,
 * which is what the draft and active pages already use.
 */
const Main = styled(ProposalPageMain)(({ theme }) => ({
  paddingBottom: theme.spacing(3),
  paddingTop: theme.spacing(1),
}));
const PageTitleLink = styled(Link)(() => ({
  color: "inherit",
  textDecoration: "none",
})) as typeof Link;
const ButtonContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
}));
const MessageButton = styled(IconButton)(() => ({
  height: 40,
  width: 40,
}));
const MessageButtonLabel = styled(Typography)(() => ({
  fontSize: "0.6em",
}));
const CommentsPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
}));
const Drawer = styled(MuiDrawer)(() => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    marginTop: "64px",
    width: DRAWER_WIDTH,
    boxSizing: "border-box",
  },
}));
const CommentsPanel = styled(Drawer, { shouldForwardProp: (prop) => prop !== "closeAfterTransition" })(() => ({}));
const CommentsPanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: theme.spacing(2),
  width: DRAWER_WIDTH,
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
const ArchivedHeader = styled(ProposalTabsHeader)(() => ({
  paddingLeft: 0,
}));
// The fee-schedule tab used to climb out of the pane's gutter here, on the left only, with a negative
// margin and a width grown to match. Every tab's content is flush now — `paneFlushContent`, declared
// by the pane — so this had nothing left to do, and doubled up with it on the side it did cover.
// #endregion

export default function ArchivedProposalPage() {
  // #region Refs
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const { id: idParam, pageTab: pageTabParam } = useParams();
  const navigate = useNavigate();

  // Proposal id is required
  if (!idParam) {
    throw new Response("Not Found", { status: 404 });
  }

  // #region State
  const [currentCommentField, setCurrentCommentField] = useState<ProposalField | undefined>();
  // The tabs below read the pane's width, not the window's — the comments panel takes a column of it.
  const { paneRef, paneWidth } = useWorkPaneSize();
  const paneTheme = useWorkPaneTheme(paneWidth);
  const canSeatSidePanel = useProposalPageCanSeatSidePanel();
  /**
   * The comments panel opens with the page — an archived proposal is a record, and the discussion is
   * most of what there is left to read on it — but only where a panel can be SEATED beside the
   * content. On a phone it cannot: the panel took the row and the tab behind it was left drawing its
   * policy list in the 100px that remained, with the proposal itself unreadable until the reader found
   * the collapse arrow. `useProposalPageCanSeatSidePanel` is the same question the page already asks
   * before reserving the panel's width. The active and draft pages start closed at every width
   * (`initialProposalPagePanelState`), so this is the only page that had to be told.
   */
  const [commentsPanelIsOpen, setCommentsPanelIsOpen] = useState<boolean>(canSeatSidePanel);
  const [commentsPanelTitle, setCommentsPanelTitle] = useState<string | undefined>();
  // #endregion

  // #region Globals
  const proposalId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const activeTab = useMemo(() => {
    const tab = pageTabParam as PageTab | undefined;
    return tab ?? PageTab.Details;
  }, [pageTabParam]);

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  // #region Proposal Queries
  const proposalJson = useSelector(getServerProposalById(proposalId));
  const proposal = useMemo(() => {
    if (!proposalJson || isProposalLoadIssue(proposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: proposalJson,
      currentUser: session.user,
    });
  }, [proposalJson, session.user]);

  const proposalQuery = useGetProposalByIdQuery(
    !proposalId || proposalId.isEmpty ? skipToken : { proposalId: proposalId?.value ?? Guid.EMPTY.value, session },
  );

  // #region Comments Queries
  const commentThreads = useSelector(getCommentThreadsByProposal(proposal));
  const isLoadingCommentThreads = useSelector(getIsLoadingCommentThreadsByProposal(proposal));
  const wasErrorLoadingCommentThreads = useSelector(getErrorLoadingCommentThreadsByProposal(proposal));
  const isLoading = proposalQuery.isLoading || isLoadingCommentThreads;

  // #region Lifecycle
  useEffect(() => {
    // Proposal invalid/not found
    const shouldShowNotFound =
      Boolean(proposalId) && !proposalId?.isEmpty && proposal === null && !proposalQuery.isLoading;

    if (shouldShowNotFound && idParam) {
      enqueueSnackbar("Proposal not found", { variant: "warning" });
      navigate("/proposals/archived", { replace: true });
    }
  }, [activeTab, idParam, navigate, proposal, proposalId, proposalQuery.isLoading]);

  // #endregion

  // #region Functions
  function handleOpenCommentsPanel(field: ProposalField, commentsPanelTitle?: string) {
    setCommentsPanelIsOpen((prev) => (field === currentCommentField ? !prev : true));
    setCurrentCommentField(field);
    setCommentsPanelTitle(commentsPanelTitle);
  }

  // Comment threads bootstrap
  useEffect(() => {
    if (
      !proposal ||
      proposal.supersededById ||
      isLoadingCommentThreads ||
      wasErrorLoadingCommentThreads ||
      commentThreads
    )
      return;
    dispatch(populateCommentThreadsByProposal({ proposal, session }));
  }, [proposal, isLoadingCommentThreads, wasErrorLoadingCommentThreads, commentThreads, dispatch, session]);

  // Account view switching
  useEffect(() => {
    if (!proposal) return;
    if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(proposal.clientLeader) &&
      session.context?.viewingAsVendor &&
      proposal.clientLeader?.userId.isEqualTo(session.user?.id)
    ) {
      session.setAccountViewType(AccountType.Client);
    } else if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      session.setAccountViewType(AccountType.Vendor);
    }
  }, [proposal, proposal?.id?.value, session]);

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    setCommentsPanelIsOpen(false);
    setCurrentCommentField(undefined);
    setCommentsPanelTitle(undefined);
    navigate(`/proposals/archived/view/${proposalId?.value}/${newTab}`);
  }

  const handleCloseCommentsPanel = () => {
    setCommentsPanelIsOpen(false);
  };

  function getCommentsPanelTitle(): string {
    if (commentsPanelTitle) return `${commentsPanelTitle} Comments`;
    return `${HumanReadableProposalFieldName[currentCommentField?.name ?? ProposalField.General.name]} Comments`;
  }

  function renderComments() {
    if (proposal?.id && commentThreads) {
      return (
        <Comments
          proposal={proposal}
          commentThreads={commentThreads}
          readOnly
          showMarkAllRead={false}
        />
      );
    }
  }

  // #region Render
  const isFeeScheduleTab = activeTab === PageTab.FeeSchedule;

  // Permissions first, and separately from the proposal's own loading. Folding them together is what
  // made a failed permissions lookup indistinguishable from one still in flight, so the refusal below
  // was unreachable and the reader waited forever.
  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;
  if (isLoading)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );

  return (
    <WorkPaneWidthContext.Provider value={paneWidth}>
    <ThemeProvider theme={paneTheme}>
    <PageContainer>
      <Main ref={paneRef} open={commentsPanelIsOpen}>
        <StickyPageHeader>
          <HeaderContainer>
            <TitleContainer>
              <PageTitle variant="h4">
                <PageTitleBreadcrumb>
                  <Tooltip title="Back to Proposals" placement="bottom">
                    <PageTitleLink component={RouterLink} to="/proposals/archived">
                      Proposals
                    </PageTitleLink>
                  </Tooltip>
                  &nbsp;&gt;
                </PageTitleBreadcrumb>
                Archived Proposal
              </PageTitle>
            </TitleContainer>
            <ButtonContainer>
              <MessageButton
                size="medium"
                color="primary"
                disabled={!proposal?.id || !proposal || !commentThreads}
                onClick={() => handleOpenCommentsPanel(ProposalField.General)}
              >
                <Badge
                  variant="standard"
                  color="secondary"
                  overlap="rectangular"
                  invisible={!commentThreads?.length}
                >
                  <CommentIcon fontSize="medium" />
                </Badge>
              </MessageButton>
              <MessageButtonLabel
                variant="button"
                color={!proposal?.id || !proposal || !commentThreads ? "darkgray" : "primary"}
              >
                Comments
              </MessageButtonLabel>
            </ButtonContainer>
          </HeaderContainer>
          <ArchivedHeader>
            <TabsContainer
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
              allowScrollButtonsMobile
              value={activeTab}
              onChange={handleTabChange}
            >
              <ProposalTab value={PageTab.Details} label={<ArchivedBadge category={ProposalFieldCategory.Details} />} />
              <ProposalTab value={PageTab.ClientTeam} label="Client Team" />
              <ProposalTab value={PageTab.VendorTeam} label={<ArchivedBadge title="Vendor Team" category={ProposalFieldCategory.Team} />} />
              <ProposalTab
                value={PageTab.FeeSchedule}
                label={<ArchivedBadge category={ProposalFieldCategory.FeeSchedule} />}
              />
              <ProposalTab
                value={PageTab.Conflicts}
                label={<ArchivedBadge category={ProposalFieldCategory.Conflicts} />}
              />
              <ProposalTab
                value={PageTab.Policies}
                label={<ArchivedBadge category={ProposalFieldCategory.Policies} />}
              />
              <ProposalTab
                value={PageTab.Discount}
                label={<ArchivedBadge category={ProposalFieldCategory.Discount} />}
              />
            </TabsContainer>
          </ArchivedHeader>
        </StickyPageHeader>
        <Content {...paneFlushContent}>
          <TabContent disableBottomPadding={isFeeScheduleTab}>
            {proposal && (
              <>
                {activeTab === PageTab.Details && (
                  <DetailsTab proposal={proposal} />
                )}
                {activeTab === PageTab.ClientTeam && (
                  <ClientTeamTab proposal={proposal} />
                )}
                {activeTab === PageTab.VendorTeam && (
                  <TeamTab entityId={session.user!.legalEntityId} proposal={proposal} />
                )}
                {activeTab === PageTab.FeeSchedule && (
                  <FeeScheduleTab proposal={proposal} />
                )}
                {activeTab === PageTab.Conflicts && (
                  <ConflictsTab proposal={proposal} />
                )}
                {activeTab === PageTab.Policies && (
                  <PoliciesTab proposal={proposal} />
                )}
                {activeTab === PageTab.Discount && (
                  <DiscountTab proposal={proposal} />
                )}
              </>
            )}
          </TabContent>
        </Content>
      </Main>
      {/* The pane's own column in the page row. A persistent drawer paints over its neighbour rather
          than displacing it, so without this the content laid itself out against width the pane was
          about to cover. */}
      <SidePanelColumn
        style={{ width: canSeatSidePanel && commentsPanelIsOpen && proposal && commentThreads ? DRAWER_WIDTH : 0 }}
      >
      {proposal && commentThreads && (
        <CommentsPanel open={commentsPanelIsOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
          <CommentsPanelContainer>
            <CommentsPanelContent>
              <TitleBar>
                <Typography variant="h5">{getCommentsPanelTitle()}</Typography>
                <Tooltip title="Minimize comments panel">
                  <IconButton size="medium" onClick={handleCloseCommentsPanel}>
                    <KeyboardTabIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
              </TitleBar>
              {renderComments()}
            </CommentsPanelContent>
          </CommentsPanelContainer>
        </CommentsPanel>
      )}
      </SidePanelColumn>
    </PageContainer>
    </ThemeProvider>
    </WorkPaneWidthContext.Provider>
  );
}
